<template>
  <vx-card v-if="session" no-shadow class="eodSessionViewOnly">
    <h5 class="mb-6">Overview</h5>

    <div class="vx-row mb-6">
      <div class="vx-col sm:w-1/5 w-full">
        <span>Venue</span>
      </div>
      <div class="vx-col sm:w-4/5 w-full">
        <code>{{ session.venueName }}</code>
      </div>
    </div>

    <div class="vx-row mb-6">
      <div class="vx-col sm:w-1/5 w-full">
        <span>Submitted by</span>
      </div>
      <div class="vx-col sm:w-4/5 w-full">
        <code>{{ session.submittedAction ? session.submittedAction.userDisplayName : 'N/A' }}</code>
      </div>
    </div>

    <div v-if="session.eodTemplate" class="vx-row mb-6">
      <div class="vx-col sm:w-1/5 w-full">
        <span>Template version</span>
      </div>
      <div class="vx-col sm:w-4/5 w-full">
        <code>{{ session.eodTemplate.version }}</code>
      </div>
    </div>

    <div class="vx-row mb-6">
      <div class="vx-col sm:w-1/5 w-full">
        <span>Status</span>
      </div>
      <div class="vx-col sm:w-4/5 w-full">
        <vs-chip
          style="color: #111"
          :color="session.status === 'SentMail' ? 'success' : session.status === 'Submitted' ? 'warning' : 'danger'"
        >
          {{ statusLabel(session.status) }}
        </vs-chip>
      </div>
    </div>

    <vs-divider />

    <h5 class="mb-6">When</h5>

    <div class="vx-row mb-6">
      <div class="vx-col sm:w-1/5 w-full">
        <span>Date (opening date)</span>
      </div>
      <div class="vx-col sm:w-4/5 w-full">
        <code>{{ session.date | date }}</code>
      </div>
    </div>

    <div class="vx-row mb-6">
      <div class="vx-col sm:w-1/5 w-full">
        <span>Closing time</span>
      </div>
      <div class="vx-col sm:w-4/5 w-full">
        <code>{{ session.closingTime || 'N/A' }}</code>
      </div>
    </div>

    <div class="vx-row mb-6">
      <div class="vx-col sm:w-1/5 w-full">
        <span>Last guest out</span>
      </div>
      <div class="vx-col sm:w-4/5 w-full">
        <code>{{ session.lastGuestOutTime || 'N/A' }}</code>
      </div>
    </div>

    <template v-if="session.eodTemplate && session.eodTemplate.questions">
      <vs-divider />
      <h5 class="mb-6">Answers</h5>

      <template v-for="(question, qIdx) in sortedQuestions">
        <!-- Rating question -->
        <div v-if="question.type === 'rating'" :key="'rating-' + question.id" class="vx-row mb-6">
          <div class="vx-col sm:w-1/5 w-full">
            <span>{{ question.title }}</span>
          </div>
          <div class="vx-col sm:w-4/5 w-full">
            <div @wheel.prevent class="rating-slider-readonly">
              <vs-slider
                :min="question.minValue || 1"
                :max="question.maxValue || 5"
                :value="question.answer || 0"
                disabled
              />
            </div>
            <span>
              Rating: <b>{{ question.answer || 0 }}/{{ question.maxValue || 5 }}</b>
              <template v-if="getLabelForRating(question)"> ({{ getLabelForRating(question) }})</template>
            </span>
            <div v-if="question.comment" class="mt-2">
              <vs-textarea
                disabled
                height="80px"
                class="inputx w-full"
                label="Comment"
                :value="question.comment"
              />
            </div>
          </div>
        </div>

        <!-- Text question -->
        <div v-else-if="question.type === 'text'" :key="'text-' + question.id" class="vx-row mb-6">
          <div class="vx-col sm:w-1/5 w-full">
            <span>{{ question.title }}</span>
          </div>
          <div class="vx-col sm:w-4/5 w-full">
            <vs-textarea
              disabled
              height="120px"
              class="inputx w-full"
              :label="question.title"
              :value="question.answer || ''"
            />
          </div>
        </div>

        <!-- Time question -->
        <div v-else-if="question.type === 'time'" :key="'time-' + question.id" class="vx-row mb-6">
          <div class="vx-col sm:w-1/5 w-full">
            <span>{{ question.title }}</span>
          </div>
          <div class="vx-col sm:w-4/5 w-full">
            <code>{{ question.answer || 'N/A' }}</code>
          </div>
        </div>

        <!-- Yes/No question -->
        <div v-else-if="question.type === 'yesNo'" :key="'yesno-' + question.id" class="vx-row mb-6">
          <div class="vx-col sm:w-1/5 w-full">
            <span>{{ question.title }}</span>
          </div>
          <div class="vx-col sm:w-4/5 w-full">
            <span>
              Answer: <b>{{ question.answer ? 'Yes' : 'No' }}</b>
            </span>
            <div v-if="question.comment" class="mt-2">
              <vs-textarea
                disabled
                height="80px"
                class="inputx w-full"
                label="Comment"
                :value="question.comment"
              />
            </div>
          </div>
        </div>

        <!-- Photo upload question -->
        <div v-else-if="question.type === 'photoUpload'" :key="'photo-' + question.id" class="vx-row mb-6">
          <div class="vx-col lg:w-1/1 w-full">
            <h5>{{ question.title }}</h5>
            <vs-row>
              <img
                class="ml-3 mt-2 mb-2 rounded-lg shadow"
                v-for="(photo, pIdx) in (question.imageUrls || [])"
                :key="pIdx"
                :src="photo"
                height="240"
                v-img:photoAnswers
              />
              <p v-if="!question.imageUrls || question.imageUrls.length === 0" class="ml-3 mt-2 text-grey">
                No photos uploaded
              </p>
            </vs-row>
          </div>
        </div>

        <!-- Divider between questions (except after the last one) -->
        <vs-divider v-if="qIdx < sortedQuestions.length - 1" :key="'div-' + question.id" />
      </template>
    </template>

    <template v-if="session.recipients && session.recipients.length > 0">
      <vs-divider />
      <h5 class="mb-6">Recipients</h5>
      <vs-table :data="session.recipients">
        <template slot="thead">
          <vs-th>Name</vs-th>
          <vs-th>Email</vs-th>
          <vs-th>Sent</vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr v-for="(r, idx) in data" :key="idx">
            <vs-td>{{ r.displayName }}</vs-td>
            <vs-td>{{ r.email }}</vs-td>
            <vs-td>
              <vs-chip :color="r.success ? 'success' : 'danger'">
                {{ r.success ? 'Yes' : 'No' }}
              </vs-chip>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </template>
  </vx-card>
</template>

<script>
import moment from "moment";

export default {
  data() {
    return {
      reportId: "",
    };
  },
  filters: {
    date(value) {
      if (!value) return "";
      return moment(value).format("LL");
    },
  },
  computed: {
    session() {
      return this.$store.getters["eodSessions/session"];
    },
    sortedQuestions() {
      if (!this.session || !this.session.eodTemplate || !this.session.eodTemplate.questions) return [];
      return this.session.eodTemplate.questions;
    },
  },
  methods: {
    statusLabel(status) {
      switch (status) {
        case "SentMail": return "Completed";
        case "Submitted": return "Processing";
        case "Draft": return "Draft";
        default: return status;
      }
    },
    getLabelForRating(question) {
      if (!question.labels || !Array.isArray(question.labels)) return "";
      const idx = question.answer;
      if (idx >= 0 && idx < question.labels.length) return question.labels[idx];
      return "";
    },
  },
  async created() {
    this.reportId = this.$route.params.id;
    this.$vs.loading();
    await this.$store.dispatch("eodSessions/getSession", this.reportId);
    this.$vs.loading.close();
  },
};
</script>

<style lang="scss">
.eodSessionViewOnly {
  .inputx textarea {
    height: calc(100% - 20px);
  }
  .rating-slider-readonly {
    pointer-events: none;
  }
}
</style>
